import { useIntersectionObserver } from "@2u-landing-pages/utils";
import EventClient from "@2u-landing-pages/utils/event-client";
import cn from "classnames";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getFilterGroupOptions } from "./filterAndSort";
import { FilterBarProps, FilterCfg, FilterOption } from "./FilterBar.types";
import { ButtonsFilter } from "./variations/Buttons/ButtonsFilter";
import { InlineFilter } from "./variations/Inline/InlineFilter";
import { PopupFilter } from "./variations/Popup/PopupFilter";

// @ts-ignore
import styles from "./FilterBar.module.scss";

// Custom hook to detect mobile viewport
const useBottomOverlay = (breakpoint = 450) => {
  const [bottomOverlay, setBottomOverlay] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const checkIsMobile = () => {
      setBottomOverlay(window.innerWidth < breakpoint);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, [breakpoint]);

  return bottomOverlay;
};

export const FilterBar: React.FC<FilterBarProps> = ({
  programs = [],
  filteredPrograms = [],
  filters,
  setFilters,
  numResults,
  filterGroups = ["School", "Topic", "Degree Level", "Industry", "Duration", "Weekly Commitment", "Start Date"],
  filterLabels = {},
  showOptionTotals = false,
  noRecommendedCard = false,
  variation = "",
  backgroundColor,
}) => {
  // const [filterOpened, toggleFilterBar] = useState(false);
  const [filterOpened, setFilterOpened] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showPortal, setShowPortal] = useState(false);
  const [btnsMainFilterGroup, setBtnsMainFilterGroup] = useState<[string, FilterOption[]] | undefined>(undefined);
  const numResultsRef = useRef(numResults);
  const useDropdownFilters = variation === "Inline";
  const useButtonsFilter = variation === "Tabs";
  const bottomOverlay = useBottomOverlay();
  const customStyle = (backgroundColor
    ? { ["--gallery-background-color"]: `var(${backgroundColor})` }
    : {}) as React.CSSProperties;

  const inlineFilterGroups = useMemo(() => {
    const filterGroupOptions = getFilterGroupOptions(
      filteredPrograms?.length > 0 ? filteredPrograms : programs,
      filterGroups,
    );
    if (filterGroups[0] !== btnsMainFilterGroup?.[0]) {
      setBtnsMainFilterGroup(Object.entries(filterGroupOptions)[0] as unknown as [string, FilterOption[]]);
    }

    return filterGroupOptions;
  }, [programs, filterGroups, filteredPrograms, btnsMainFilterGroup]);

  const popupFilterGroups = useMemo(() => {
    return getFilterGroupOptions(programs, filterGroups);
  }, [programs, filterGroups]);

  const btnsFilterGroups = useMemo(() => {
    const [_, ...newFilterGroups] = Object.entries(inlineFilterGroups);
    const btnsNewFilterGroups: FilterCfg = useButtonsFilter
      ? (Object.fromEntries(newFilterGroups) as FilterCfg)
      : inlineFilterGroups;
    return btnsNewFilterGroups;
  }, [inlineFilterGroups, useButtonsFilter]);

  const toggleFilterBar = (open: boolean) => {
    if (open) {
      if (bottomOverlay) {
        setShowPortal(true);
      }
      setFilterOpened(true);
      setIsClosing(false);
    } else if (filterOpened) {
      if (bottomOverlay && showPortal) {
        // Start closing animation for portal
        setIsClosing(true);
        setTimeout(() => {
          setFilterOpened(false);
          setIsClosing(false);
          setShowPortal(false); // Hide portal after animation
        }, 250); // Match animation duration
      } else {
        setFilterOpened(false);
        setIsClosing(false);
      }
    }
  };

  // Handle portal visibility when bottomOverlay changes
  useEffect(() => {
    if (bottomOverlay && filterOpened && !showPortal) {
      setShowPortal(true);
    } else if (!bottomOverlay && showPortal) {
      setShowPortal(false);
      setIsClosing(false);
    }
  }, [bottomOverlay, filterOpened, showPortal]);

  useEffect(() => {
    numResultsRef.current = numResults;
  }, [numResults]);

  useEffect(() => {
    setFilters({});
  }, [variation, setFilters]);

  const handleIntersect = useCallback(() => {
    EventClient.elementViewed({
      webElement: {
        elementType: "Landing Page",
        htmlId: String(numResultsRef.current),
        location: "Gallery",
        position: "Gallery",
        text: String(numResultsRef.current),
      },
    });
  }, [numResultsRef]);

  const addObserver = useIntersectionObserver(handleIntersect, { threshold: 0.5 });
  const setObserver = useCallback(
    (element: HTMLElement | null) => {
      if (element) addObserver(element);
    },
    [addObserver]
  );

  function onFilterChange(key: string, value: string, checked: boolean) {
    if (checked) {
      setFilters({
        ...filters,
        [key]: [...(filters[key] || []), value],
      });
    } else {
      const list = filters[key]?.filter((v: string) => v !== value);

      if (list.length === 0) {
        setFilters({
          ...filters,
          [key]: null,
        });
      } else {
        setFilters({
          ...filters,
          [key]: list,
        });
      }
    }
  }

  const handleResetFilters = () => {
    setFilters({});

    EventClient.elementClicked({
      webElement: {
        elementType: "Landing Page",
        htmlId: "",
        location: "Inline Filter",
        name: "Reset filters",
        position: "Inline Filter",
        text: "Reset filters",
      },
    });
  };

  return (
    <div
      className={cn(styles.galleryFilters, {
        [styles.noRecommendedCard]: noRecommendedCard,
        [styles.usingDropDownFilters]: useDropdownFilters,
        [styles.usingButtonsFilters]: useButtonsFilter,
      })}
      style={customStyle}
    >
      {(useDropdownFilters || useButtonsFilter) && (
        <>
          {useButtonsFilter && (
            <ButtonsFilter filterGroup={btnsMainFilterGroup} filters={filters} setFilters={setFilters} />
          )}
          <InlineFilter
            key={variation}
            filters={filters}
            setFilters={setFilters}
            numResults={numResults}
            filterLabels={filterLabels}
            selectFilterGroupOptions={inlineFilterGroups}
            useButtons={useButtonsFilter}
            btnsFilterGroups={btnsFilterGroups}
          />
        </>
      )}
      <div className={styles.popupFiltersContainer}>
        <PopupFilter
          filterOpened={filterOpened}
          isClosing={isClosing}
          bottomOverlay={bottomOverlay}
          onToggle={toggleFilterBar}
          onResetFilters={handleResetFilters}
          useButtonsFilter={useButtonsFilter}
          btnsFilterGroups={btnsFilterGroups}
          filterGroupOptions={popupFilterGroups}
          filterLabels={filterLabels}
          showOptionTotals={showOptionTotals}
          filters={filters}
          onFilterChange={onFilterChange}
        />
        <div className={styles.galleryFilterTotal} ref={setObserver}>
          <span>
            {numResults} Result{numResults === 1 ? "" : "s"}
          </span>
        </div>
      </div>
    </div>
  );
};
